import React, { CSSProperties } from 'react';
import { ReactComponent as BalanceIcon } from './balance.svg';
import { ReactComponent as BestIcon } from './best.svg';
import { BulletproofCaptureBuilder } from './Builders/BulletproofCaptureBuilder';
import { CallbackKingBuilder } from './Builders/CallbackKingBuilder';
import { TemplateBuilder } from './Builders/Components/TemplateBuilderFactory';
import { FamousAudienceExtractorBuilder } from './Builders/FamousAudienceExtractorBuilder';
import { FreeEbookDownloadBuilder } from './Builders/FreeEbookDownloadBuilder';
import { GiftGiveawayBuilder } from './Builders/GiftGiveawayBuilder';
import { LongestFollowUpFunnelBuilder } from './Builders/LongestFollowUpFunnelBuilder';
import { MoreReviewsFunnelBuilder } from './Builders/MoreReviewsFunnelBuilder';
import { UltimateWelcomeFunnelBuilder } from './Builders/UltimateWelcomeFunnelBuilder';
import { ReactComponent as BulletproofCaptureIcon } from './bulletproof_capture.svg';
import { ReactComponent as CallbackKingIcon } from './callback_king.svg';
import { ReactComponent as DonkeyIcon } from './donkey.svg';
import { ReactComponent as FlashIcon } from './flash.svg';
import { ReactComponent as FreeEbookDownloadIcon } from './free_ebook_download.svg';
import { ReactComponent as GiftGiveawayIcon } from './gift_giveaway.svg';
import { ReactComponent as HelloIcon } from './hello.svg';
import HeroImage from './hero_img.png';
import { ReactComponent as InboundCallIcon } from './inbound-call.svg';
import { ReactComponent as IncreaseIcon } from './increase.svg';
import { ReactComponent as LongestFollowUpFunnelIcon } from './longest_follow_up_funnel.svg';
import { ReactComponent as MagnetIcon } from './magnet.svg';
import { ReactComponent as MoreReviewsFunnelIcon } from './more_reviews_funnel.svg';
import { ReactComponent as RocketIcon } from './rocket.svg';
import { ReactComponent as SoftIcon } from './soft.svg';
import { ReactComponent as UnicornIcon } from './unicorn.svg';

export type AutomationTemplateChannel =
  | 'facebook'
  | 'instagram'
  | 'email'
  | 'webchat';
interface Tag {
  icon: React.ComponentType<any>;
  title: string;
}
export interface AutomationTemplateDefinition {
  icon: React.ComponentType<any>;
  title: string;
  subtitle?: string;
  description: string;
  tags: readonly Tag[];
  templateBuilder: TemplateBuilder;
  maxFollowUpCounts: {
    maxUnicornSMSQuantity: number;
    maxDonkeySMSQuantity: number;
    maxDonkeyEmailQuantity: number;
    maxUnicornEmailQuantity: number;
  };
  channels: Partial<Record<AutomationTemplateChannel, boolean>>;
  highlightStyle: CSSProperties;
  hero?: {
    buttonLabel: string;
    imageSrc: string;
    benefits: Tag[];
  };
}

export const AUTOMATION_TEMPLATES = {
  ultimate_welcome_funnel: {
    icon: HelloIcon,
    title: 'Ultimate Welcome Funnel',
    tags: [
      { icon: IncreaseIcon, title: 'Maximize the value of your Audience!' },
    ],
    highlightStyle: {
      background:
        'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #D2F9FF 8.88%, #F2E0CF 41.67%, #8F94FF 100%)',
    },
    templateBuilder: UltimateWelcomeFunnelBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 3,
      maxDonkeySMSQuantity: 1,
      maxUnicornEmailQuantity: 3,
      maxDonkeyEmailQuantity: 1,
    },

    channels: {
      facebook: true,
      instagram: true,
      sms: true,
    },
    description: 'Roll Out the Red Carpet for Unicorn Leads',
    hero: {
      buttonLabel: 'Create',
      imageSrc: HeroImage,
      benefits: [
        {
          icon: UnicornIcon,
          title: 'Talk to your most engaged leads and prospects!',
        },
        {
          icon: DonkeyIcon,
          title: 'Save time chatting with donkey leads',
        },
      ],
    },
  } as AutomationTemplateDefinition,
  longest_follow_up_funnel: {
    icon: LongestFollowUpFunnelIcon,
    title: 'The Longest and Most Persistent Follow Up Funnel Ever',
    subtitle: '(14 messages over 2 weeks)',
    tags: [{ icon: RocketIcon, title: 'Sell more quickly!' }],
    highlightStyle: {},
    templateBuilder: LongestFollowUpFunnelBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 14,
      maxDonkeySMSQuantity: 7,
      maxUnicornEmailQuantity: 14,
      maxDonkeyEmailQuantity: 7,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Create maximum urgency and FOMO through rapid messaging cadence. ' +
      'Ideal for brands with high affinity fans. ',
  } as AutomationTemplateDefinition,
  free_ebook_download: {
    icon: FreeEbookDownloadIcon,
    title: 'Free E-book/Whitepaper Download Offer',
    tags: [{ icon: SoftIcon, title: 'Softer Sell!' }],
    highlightStyle: {
      background:
        'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FEEFC7 5.58%, #D0F0D9 44.46%, #86C7C3 100%)',
    },
    templateBuilder: FreeEbookDownloadBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 3,
      maxDonkeySMSQuantity: 0,
      maxUnicornEmailQuantity: 3,
      maxDonkeyEmailQuantity: 0,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Great for Solution Sales - lead with information, build credibility ' +
      'first, and close later.',
  } as AutomationTemplateDefinition,
  famous_audience_extractor: {
    icon: FreeEbookDownloadIcon,
    title: 'Our Famous Instagram and Facebook Audience Extractor',
    tags: [{ icon: MagnetIcon, title: 'Never miss a lead' }],
    highlightStyle: {
      background:
        'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FFF3BD 15.24%, #FFEA91 50%, #ECB44E 100%)',
    },
    templateBuilder: FamousAudienceExtractorBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 0,
      maxDonkeySMSQuantity: 0,
      maxUnicornEmailQuantity: 0,
      maxDonkeyEmailQuantity: 0,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Once you connect your Facebook and Instagram accounts, this runs in ' +
      'the background and collects contact information for anyone who engages.',
  } as AutomationTemplateDefinition,
  more_reviews_funnel: {
    icon: MoreReviewsFunnelIcon,
    title: 'The Get More (Positive!) Reviews Funnel',
    tags: [{ icon: BestIcon, title: 'Better Overall Score!' }],
    highlightStyle: {},
    templateBuilder: MoreReviewsFunnelBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 1,
      maxDonkeySMSQuantity: 1,
      maxUnicornEmailQuantity: 1,
      maxDonkeyEmailQuantity: 1,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Get more positive reviews and fewer bad reviews by only soliciting ' +
      'reviews from your top fans. ',
  } as AutomationTemplateDefinition,
  callback_king: {
    icon: CallbackKingIcon,
    title: 'The Callback King',
    tags: [{ icon: InboundCallIcon, title: 'More Inbound Calls!' }],
    highlightStyle: {},
    templateBuilder: CallbackKingBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 1,
      maxDonkeySMSQuantity: 0,
      maxUnicornEmailQuantity: 1,
      maxDonkeyEmailQuantity: 0,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'This sequence generates the most inbound calls on earth! Send text ' +
      'messages with a GREAT offer with unusually high call-back rates. Feed ' +
      'your Sales team live, high quality leads. ',
  } as AutomationTemplateDefinition,
  bulletproof_capture: {
    icon: BulletproofCaptureIcon,
    title: 'Bulletproof GDPR-compliant Email and Phone Number Opt-in Capture',
    tags: [{ icon: BalanceIcon, title: '1000% compliance!' }],
    highlightStyle: {},
    templateBuilder: BulletproofCaptureBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 0,
      maxDonkeySMSQuantity: 0,
      maxDonkeyEmailQuantity: 0,
      maxUnicornEmailQuantity: 0,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Breathe easy and send the privacy lawyers packing with this ' +
      'totally GDPR-compliant lead capture automation.',
  } as AutomationTemplateDefinition,
  gift_giveaway: {
    icon: GiftGiveawayIcon,
    title: 'Free Gift Giveaway with Mailing Address Collection ',
    tags: [{ icon: FlashIcon, title: 'Super-charge engagement!' }],
    highlightStyle: {},
    templateBuilder: GiftGiveawayBuilder,
    maxFollowUpCounts: {
      maxUnicornSMSQuantity: 3,
      maxDonkeySMSQuantity: 0,
      maxUnicornEmailQuantity: 3,
      maxDonkeyEmailQuantity: 0,
    },

    channels: {
      facebook: true,
      instagram: true,
    },
    description:
      'Offer entry into a giveaway, ask for their information and thank them for their participation.',
  } as AutomationTemplateDefinition,
  // GE 2022-06-15: Future templates awaiting definition by Dan
  // thirty_day_challenge: {
  //   icon: ThirtyDayChallengeIcon,
  //   title: '30-day Challenge',
  //   tags: [{ icon: HeartIcon, title: 'Turn newcomers into superfans!' }],
  //   highlightStyle: {},
  //   templateBuilder: ThirtyDayChallengeBuilder,
  //   channels: {
  //     facebook: true,
  //     instagram: true,
  //   },
  //   description:
  //     'Collect or upload signups for a challenge, and deliver the content over the course of 30 (or more, or fewer!) days.',
  // } as AutomationTemplateDefinition,
  // one_off_sms_blast: {
  //   icon: OneOffSmsBlastIcon,
  //   title: '1-off SMS Chat Blast',
  //   tags: [
  //     {
  //       icon: HandshakeIcon,
  //       title: 'Re-engaging your existing SMS subscribers',
  //     },
  //   ],
  //   highlightStyle: {},
  //   templateBuilder: OneOffSmsBlastBuilder,
  //   channels: {
  //     facebook: true,
  //     instagram: true,
  //   },
  //   description:
  //     'Collect or upload signups for a challenge, and deliver the content over the course of 30 (or more, or fewer!) days. ',
  // } as AutomationTemplateDefinition,
} as const;

export type AutomationTemplateName =
  | keyof typeof AUTOMATION_TEMPLATES
  | 'empty_automation'
  | 'email_sold_automation';

export interface AutomationTemplateCategoryDefinition {
  categoryName: string;
  tags: readonly Tag[];
  templateNames: AutomationTemplateName[];
  highlightTemplates?: boolean;
}

export const AUTOMATION_TEMPLATES_BY_CATEGORY: AutomationTemplateCategoryDefinition[] =
  [
    {
      categoryName: 'Get started with these easy-to-use templates',
      tags: [],
      templateNames: [
        'ultimate_welcome_funnel',
        'famous_audience_extractor',
        'free_ebook_download',
      ],
      highlightTemplates: true,
    },
    {
      categoryName: 'Sales Outreach',
      tags: [],
      templateNames: [
        'longest_follow_up_funnel',
        'callback_king',
        'bulletproof_capture',
      ],
    },
    {
      categoryName: 'Lead Qualification',
      tags: [],
      templateNames: [
        'famous_audience_extractor',
        'more_reviews_funnel',
        'free_ebook_download',
        'gift_giveaway',
      ],
    },
    {
      categoryName: 'Appointment Booking',
      tags: [],
      templateNames: ['longest_follow_up_funnel', 'callback_king'],
    },
    {
      categoryName: 'List Building',
      tags: [],
      templateNames: [
        'bulletproof_capture',
        'free_ebook_download',
        'gift_giveaway',
      ],
    },
    {
      categoryName: 'Marketing',
      tags: [],
      templateNames: [
        'famous_audience_extractor',
        'more_reviews_funnel',
        'callback_king',
        'bulletproof_capture',
        'free_ebook_download',
        'gift_giveaway',
      ],
    },
  ];

export const HERO_TEMPLATES: AutomationTemplateName[] = [
  'ultimate_welcome_funnel',
];
